import React from 'react';
import { Box } from '@mui/material';
import {
  signalBorders,
  signalColors,
  signalFonts,
  signalRadii,
  signalStatus,
  signalSurfaces,
  signalText,
} from '@/theme/foundations/tokens';
import {
  Mono,
  ProgressTrack,
  ScreenFrame,
  SectionLabel,
  SignalButton,
  StatusPill,
  Waveform,
} from '@/common/components/signal';
import { stemsWave } from '../fixtures';

/** Queued-locally rows on the offline card. */
const QUEUED = ['Nightdrive.als', 'Renders/rough master v14.wav'];

/** The two conflicting copies, side by side. `mine` gets the selected ring. */
const COPIES: {
  rubric: string;
  color: string;
  name: string;
  meta: string[];
  mine?: boolean;
}[] = [
  {
    rubric: 'YOUR COPY · LOCAL',
    color: signalColors.accentBright,
    name: 'Halcyon.flp',
    meta: ['470 MB', 'SAVED 12 MIN AGO', '+2 TRACKS, +1 RENDER'],
    mine: true,
  },
  {
    rubric: 'REMOTE · ADA REIN',
    color: signalColors.cyan,
    name: 'Halcyon.flp',
    meta: ['466 MB', 'PUSHED 40 MIN AGO', 'MIX AUTOMATION EDITS'],
  },
];

/** Post-failure tally: what did land, and what is still owed. */
const TALLY: [string, string, string, string][] = [
  ['✓', signalColors.green, '410 of 412 files uploaded', '1.78 GB'],
  ['◴', signalColors.amber, '1 file waiting on the retry', '62 MB'],
];

/**
 * The 560px card every state on this screen is built in. Border colour is the
 * only thing that varies — the state is announced by the pill inside, not by
 * the frame, so four different failures still read as the same product.
 */
const StateCard: React.FC<{
  children: React.ReactNode;
  border?: string;
  background?: string;
  padding?: string;
  sx?: object;
}> = ({
  children,
  border = signalBorders.medium,
  background = signalSurfaces.screen,
  padding = '28px',
  sx,
}) => (
  <Box
    sx={{
      width: 560,
      maxWidth: '100%',
      borderRadius: `${signalRadii['3xl']}px`,
      border: `1px solid ${border}`,
      background,
      boxShadow: '0 30px 70px -36px rgba(0,0,0,0.95)',
      padding,
      display: 'flex',
      flexDirection: 'column',
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CardTitle: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 26,
}) => (
  <Box
    component="h3"
    sx={{
      margin: '0 0 8px',
      fontFamily: signalFonts.display,
      fontWeight: 800,
      fontSize: `${size}px`,
      letterSpacing: '-0.03em',
      lineHeight: 1.05,
    }}
  >
    {children}
  </Box>
);

const CardBody: React.FC<{ children: React.ReactNode; sx?: object }> = ({
  children,
  sx,
}) => (
  <Box
    component="p"
    sx={{
      margin: '0 0 20px',
      fontSize: '13px',
      lineHeight: 1.6,
      color: signalText.secondary,
      ...sx,
    }}
  >
    {children}
  </Box>
);

/**
 * 13 — Empty & error states.
 *
 * The screens that decide whether the product is trusted. Each of the four
 * refuses to be only bad news: the empty vault offers two ways in, the conflict
 * keeps both saves rather than making you choose destructively, the offline
 * agent says outright that nothing is lost, and the failed transfer reports the
 * 410 files that *did* land and the byte offset it will resume from. A red
 * border with no next action would be cheaper to build and worse to receive.
 */
export const Screen13States: React.FC = () => (
  <ScreenFrame
    index="13"
    title="Empty & error states"
    note="THE UNHAPPY PATHS"
    bare
  >
    <Box
      sx={{
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
      }}
    >
      <StateCard
        padding="44px 36px"
        sx={{ alignItems: 'center', textAlign: 'center' }}
      >
        {/* A waveform with nothing behind it — the one ornament that belongs
            on an empty vault, since it's what the vault is *for*. */}
        <Waveform
          bars={stemsWave}
          height={56}
          barWidth={4}
          gap={3}
          sx={{ marginBottom: '24px', opacity: 0.5 }}
        />
        <CardTitle size={30}>Nothing in the vault yet</CardTitle>
        <CardBody
          sx={{ margin: '0 0 24px', fontSize: '13.5px', maxWidth: '44ch' }}
        >
          Drop a session folder here, or let the desktop agent scan the folders
          you already use.
        </CardBody>
        <Box sx={{ display: 'flex', gap: '10px', marginBottom: '26px' }}>
          <SignalButton
            glow={false}
            sx={{ padding: '11px 18px', borderRadius: `${signalRadii.md}px` }}
          >
            Upload a session
          </SignalButton>
          <SignalButton
            variant="secondary"
            sx={{
              padding: '11px 18px',
              borderRadius: `${signalRadii.md}px`,
              background: signalSurfaces.fillHover,
              borderColor: signalBorders.stronger,
              '&:hover': { background: 'rgba(255,255,255,0.11)' },
            }}
          >
            Scan my folders
          </SignalButton>
        </Box>
        <Box
          sx={{
            width: '100%',
            border: `1px dashed ${signalBorders.control}`,
            borderRadius: `${signalRadii['2xl']}px`,
            padding: '22px',
            fontFamily: signalFonts.mono,
            fontWeight: 500,
            fontSize: '11px',
            color: signalText.muted,
          }}
        >
          DROP A .ALS / .LOGICX / .PTX / .FLP FOLDER
        </Box>
      </StateCard>

      <StateCard
        border={signalStatus.conflict.border}
        background={signalSurfaces.screen}
      >
        <StatusPill
          status="conflict"
          dot
          sx={{
            marginBottom: '16px',
            padding: '5px 10px',
            alignSelf: 'flex-start',
          }}
        >
          CONFLICT
        </StatusPill>
        <CardTitle>Halcyon.flp changed in two places</CardTitle>
        <CardBody>
          You and Ada Rein both saved after v7. Pick one to keep — the other is
          stored as a branch version, never deleted.
        </CardBody>

        <Box sx={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
          {COPIES.map((copy) => (
            <Box
              key={copy.rubric}
              sx={{
                flex: 1,
                borderRadius: `${signalRadii.xl}px`,
                padding: '15px',
                ...(copy.mine
                  ? {
                      border: `1px solid ${signalColors.accent}`,
                      background: 'rgba(255,106,31,0.07)',
                      boxShadow: '0 0 0 3px rgba(255,106,31,0.14)',
                    }
                  : { border: `1px solid ${signalBorders.medium}` }),
              }}
            >
              <Mono
                size={10}
                weight={600}
                color={copy.color}
                sx={{ display: 'block', marginBottom: '9px' }}
              >
                {copy.rubric}
              </Mono>
              <Box
                sx={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: signalText.primary,
                }}
              >
                {copy.name}
              </Box>
              <Mono
                size={10.5}
                sx={{
                  display: 'block',
                  marginTop: '6px',
                  lineHeight: 1.6,
                }}
              >
                {copy.meta.map((line) => (
                  <Box component="span" key={line} sx={{ display: 'block' }}>
                    {line}
                  </Box>
                ))}
              </Mono>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <SignalButton
            variant="ghost"
            sx={{ padding: '10px 14px', borderRadius: `${signalRadii.md}px` }}
          >
            Keep both as branches
          </SignalButton>
          <SignalButton
            glow={false}
            sx={{ padding: '10px 18px', borderRadius: `${signalRadii.md}px` }}
          >
            Keep mine, push v8
          </SignalButton>
        </Box>
      </StateCard>

      <StateCard border={signalStatus.behind.border}>
        <StatusPill
          status="behind"
          dot
          sx={{
            marginBottom: '16px',
            padding: '5px 10px',
            alignSelf: 'flex-start',
          }}
        >
          AGENT OFFLINE
        </StatusPill>
        <CardTitle>Sync is paused</CardTitle>
        <CardBody>
          The desktop agent hasn&apos;t reported since 11:42. Your last 2 saves
          are waiting locally — nothing is lost.
        </CardBody>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            marginBottom: '20px',
          }}
        >
          {QUEUED.map((file) => (
            <Box
              key={file}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '12.5px',
                color: signalText.secondary,
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '999px',
                  background: signalColors.amber,
                  flex: 'none',
                }}
              />
              {file}
              <Mono
                size={10.5}
                color={signalColors.amber}
                sx={{ marginLeft: 'auto' }}
              >
                QUEUED LOCALLY
              </Mono>
            </Box>
          ))}
        </Box>

        <Box sx={{ marginTop: 'auto', display: 'flex', gap: '10px' }}>
          <SignalButton
            glow={false}
            sx={{ padding: '11px 18px', borderRadius: `${signalRadii.md}px` }}
          >
            Restart agent
          </SignalButton>
          <SignalButton
            variant="secondary"
            sx={{
              padding: '11px 18px',
              borderRadius: `${signalRadii.md}px`,
              background: 'transparent',
              color: signalText.secondary,
              borderColor: signalBorders.stronger,
            }}
          >
            View logs
          </SignalButton>
        </Box>
      </StateCard>

      <StateCard>
        <SectionLabel size={10} sx={{ display: 'block', marginBottom: '16px' }}>
          TRANSFER FAILED
        </SectionLabel>

        <Box
          sx={{
            border: '1px solid rgba(255,92,92,0.3)',
            background: 'rgba(255,92,92,0.06)',
            borderRadius: `${signalRadii.xl}px`,
            padding: '16px',
            marginBottom: '14px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '9px',
              marginBottom: '9px',
            }}
          >
            <Box
              component="span"
              sx={{ color: signalColors.red, fontSize: '11px' }}
            >
              !
            </Box>
            <Box
              component="span"
              sx={{ fontSize: '13px', color: signalText.primary, flex: 1 }}
            >
              Samples/Imported/vox_take_04.wav
            </Box>
            <Mono size={10.5} weight={600} color={signalColors.red}>
              FAILED
            </Mono>
          </Box>

          {/* The bar stops where the connection dropped rather than resetting
              to zero — the byte offset it resumes from is the whole point. */}
          <ProgressTrack
            value={48}
            fill={signalColors.red}
            sx={{ marginBottom: '8px' }}
          />
          <Mono size={10.5}>
            CONNECTION RESET AT 48% · 3 ATTEMPTS · RESUMES FROM 48%
          </Mono>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '11px',
            marginBottom: '18px',
          }}
        >
          {TALLY.map(([glyph, color, label, size]) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '12.5px',
                color: signalText.secondary,
              }}
            >
              <Box component="span" sx={{ color, fontSize: '11px' }}>
                {glyph}
              </Box>
              {label}
              <Mono size={10.5} sx={{ marginLeft: 'auto' }}>
                {size}
              </Mono>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            marginTop: 'auto',
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
          }}
        >
          <SignalButton
            variant="ghost"
            sx={{ padding: '10px 14px', borderRadius: `${signalRadii.md}px` }}
          >
            Skip this file
          </SignalButton>
          <SignalButton
            glow={false}
            sx={{ padding: '10px 18px', borderRadius: `${signalRadii.md}px` }}
          >
            Retry upload
          </SignalButton>
        </Box>
      </StateCard>
    </Box>
  </ScreenFrame>
);

export default Screen13States;
